import { format } from 'date-fns';
import { forwardRef } from 'react';
import QRCode from 'react-qr-code';

const numberToWords = (num) => {
   if (num === undefined || num === null) return '';

   const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
   const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

   if (num < 20) return ones[num];

   if (num < 100) {
      return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
   }

   if (num < 1000) {
      return ones[Math.floor(num / 100)] + ' hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
   }

   return num.toString();
};

const PLTCPCertificateTemplate = forwardRef(({ certificate, application, orderOfPayment, hiddenOnPrint = [] }, ref) => {
   console.log('PLTCP Template Props:', {
      certificate,
      application,
      orderOfPayment
   });

   const certificateData = certificate?.pltcpCertificateData || application;
   const treeInventory = certificateData?.treeInventory?.[0] || {};

   console.log('PLTCP Processed Data:', {
      certificateData,
      treeInventory
   });

   // Format tree description: "one (1) blown-down Acacia (Samanea saman)"
   const treeDescription = treeInventory ?
      `${numberToWords(treeInventory.numberOfTreesInventoried)} (${treeInventory.numberOfTreesInventoried}) ${treeInventory.treeStatus?.toLowerCase()} ${treeInventory.species} (${treeInventory.scientificName})`
      : '_________________';

   // Format total trees with number to words
   const totalTreesInWords = certificateData?.totalTrees ?
      `${numberToWords(certificateData.totalTrees).toUpperCase()} (${certificateData.totalTrees}) ${treeInventory.species} trees` :
      '_______';

   // Use a direct URL instead of JSON data for better QR code compatibility across devices
   const qrValue = `${window.location.origin}/verify-certificate/${certificate?.id}`;

   const formatDate = (timestamp) => {
      try {
         if (!timestamp) return null;
         if (typeof timestamp === 'string') {
            if (!isNaN(timestamp)) {
               return format(new Date(parseInt(timestamp)), 'MMMM d, yyyy');
            }
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (typeof timestamp === 'number') {
            return format(new Date(timestamp), 'MMMM d, yyyy');
         }
         if (timestamp instanceof Date) {
            return format(timestamp, 'MMMM d, yyyy');
         }
         return null;
      } catch (error) {
         console.error('Error formatting date:', error, 'timestamp:', timestamp);
         return null;
      }
   };

   return (
      <div ref={ref} className="p-8 bg-white">
         {/* First Page */}
         <div className="mb-16">
            {/* Header */}
            <div className="relative mb-6">
               <div className="flex justify-between items-center w-full mb-1">
                  <img
                     src="/denr-logo.png"
                     alt="DENR Logo"
                     className="h-16 w-auto object-contain"
                  />
                  <img
                     src="/bagong-pilipinas-logo.png"
                     alt="Bagong Pilipinas Logo"
                     className="h-16 w-auto object-contain"
                  />
               </div>
               <div className="text-center">
                  <p className="text-sm m-0 leading-tight">DEPARTMENT OF ENVIRONMENT AND NATURAL RESOURCES</p>
                  <p className="text-sm m-0 leading-tight">KAGAWARAN NG KAPALIGIRAN AT LIKAS NA YAMAN</p>
                  <p className="text-sm m-0 leading-tight">PENRO MARINDUQUE</p>
               </div>
            </div>

            <div className="text-center mb-6">
               <h1 className="text-xl font-bold mb-2">TREE CUTTING PERMIT</h1>
               <p className="text-base mb-4">NO. {certificate?.certificateNumber || '_________________'}</p>
            </div>

            {/* Introduction Text */}
            <div className="mb-6">
               <p className="text-justify">
                  Pursuant to P.D. 705, as amended, DAO 2021-11, and existing forestry laws, rules, and regulations, a
                  Tree Cutting Permit is hereby granted to:
               </p>
            </div>

            {/* Permittee Details */}
            <div className="text-center mb-6">
               <p className="text-blue-600 font-bold uppercase">DEPARTMENT OF EDUCATION</p>
               <p className="text-blue-600 font-bold">MIMAROPA REGION</p>
               <p className="text-blue-600 font-bold">SCHOOLS DIVISION OF MARINDUQUE</p>
               <p className="text-blue-600 font-bold">
                  REPRESENTED BY {certificateData?.name || '_________________'}, {' '}
                  {certificateData?.occupation || '_________________'}, {' '}
                  {certificateData?.projectLocation || '_________________'}
               </p>
            </div>

            {/* Purpose Section */}
            <div className="mb-6">
               <p className="text-center">For the Purpose of</p>
               <p className="text-center text-blue-600 font-bold">
                  {certificateData?.purpose || '_______________________________________'}
               </p>
            </div>

            {/* Location Section */}
            <div className="mb-6">
               <p className="text-center">located in</p>
               <p className="text-center text-blue-600 font-bold">
                  {certificateData?.projectAddress || '_______________________________________'}
               </p>
            </div>

            {/* Conditions Section (First 2 items) */}
            <div className="mb-6">
               <p className="mb-4">Subject to the following conditions:</p>
               <ol className="list-decimal pl-6 space-y-4">
                  <li>
                     The trees requested for removal shall be incorporated in the design of the project to be
                     rehabilitated/reconstructed, to the extent possible, to minimize loss of trees;
                  </li>
                  <li>
                     A total of <span className="underline">{totalTreesInWords}</span>, as verified by PENRO Technical Personnel shall be authorized to be cut which is
                     shown in the table below.
                  </li>
               </ol>
            </div>

            {/* Tree Table */}
            <div className="mb-6">
               <p className="font-bold mb-2">Table 1. Trees authorized to be cut:</p>
               <table className="w-full border-collapse border border-black">
                  <thead>
                     <tr>
                        <th className="border border-black p-2">Name of Species</th>
                        <th className="border border-black p-2">No. of Trees Inventoried</th>
                        <th className="border border-black p-2">Gross Volume (cu.m)</th>
                        <th className="border border-black p-2">Net Volume (cu.m)</th>
                        <th className="border border-black p-2">Tree Category</th>
                     </tr>
                  </thead>
                  <tbody>
                     {/* {treeInventory.map((tree, index) => ( */}
                     <tr>
                        <td className="border border-black p-2 text-center">
                           {treeInventory.species} ({treeInventory.scientificName})
                        </td>
                        <td className="border border-black p-2 text-center">
                           {treeInventory.numberOfTreesInventoried}
                        </td>
                        <td className="border border-black p-2 text-center">
                           {treeInventory.grossVolume?.toFixed(3)}
                        </td>
                        <td className="border border-black p-2 text-center">
                           {treeInventory.netVolume?.toFixed(3)}
                        </td>
                        <td className="border border-black p-2 text-center">
                           {treeInventory.treeCategory}
                        </td>
                     </tr>
                     {/* ))} */}
                     <tr>
                        <td className="border border-black p-2 font-bold text-center">Grand Total</td>
                        <td className="border border-black p-2 text-center">{certificateData?.totalTrees}</td>
                        <td className="border border-black p-2 text-center">{certificateData?.totalGrossVolume?.toFixed(3)}</td>
                        <td className="border border-black p-2 text-center">{certificateData?.totalNetVolume?.toFixed(3)}</td>
                        <td className="border border-black p-2"></td>
                     </tr>
                  </tbody>
               </table>
            </div>

            {/* Conditions Section (Items 3-6) */}
            <div className="mb-6">
               <ol className="list-decimal pl-6 space-y-4" start="3">
                  <li>
                     Issues that will arise from the tree cutting should be addressed directly by the permittee;
                  </li>
                  <li>
                     Prior to tree cutting operations, a placard or signboard measuring 4' x 8' shall be installed within the
                     cutting area informing the public that the tree cutting operation is authorized by the DENR and indicating
                     the name of the permittee, the purpose of tree cutting and the number of trees to be cut;
                  </li>
                  <li>
                     The tree cutting operations shall, at all times, be under the direct supervision of the PENRO
                     Marinduque, or her duly authorized representative(s);
                  </li>
                  <li>
                     The permittee shall be required to replace each tree to be cut with fifty (50) seedlings of indigenous species;
                  </li>
               </ol>
            </div>
         </div>

         {/* Second Page */}
         <div className="page-break-before">
            {/* Continue conditions */}
            <div className="mb-8">
               <ol className="list-decimal pl-6 space-y-4" start="7">
                  <li>
                     The permittee shall be required to undertake measures during and after tree cutting operations to mitigate
                     the possible impacts of the said activity to the locality and the environment; and
                  </li>
                  <li>
                     <span className="font-bold">
                        Violation of any of the terms and conditions of the permit issued shall be sufficient ground for
                        the termination/cancellation of this permit
                     </span>
                     , subject to due process without prejudice to the imposition
                     of appropriate penalties pursuant to existing regulations, and one strike policy to concerned DENR official(s)
                     and/or personnel.
                  </li>
               </ol>
            </div>

            {/* Validity Period */}
            <div className="mb-8">
               <p>
                  This permit is effective <span className="font-bold">Fifty (50) days</span> upon receipt hereof or as soon as the authorized number of trees
                  had already been cut, whichever comes first.
               </p>
            </div>

            {/* Issuance Details */}
            <div className="mb-8">
               <p>
                  Issued this _________________, 2024, at DENR PENRO Marinduque, Capitol Compound, Brgy. Bangbangalon,
                  Boac, Marinduque.
               </p>
            </div>

            {/* Signature Section */}
            <div className="mb-8">
               <p className="mb-4">By the Authority of the Secretary:</p>
               <div className="text-center mt-16">
                  {certificate?.penrCenrOfficerSignature?.data && (
                     <div className="mb-2">
                        <img
                           src={`data:${certificate.penrCenrOfficerSignature.contentType};base64,${certificate.penrCenrOfficerSignature.data}`}
                           alt="PENR Officer Signature"
                           className="mx-auto h-16 object-contain"
                        />
                     </div>
                  )}
                  <p className="font-bold">IMELDA M. DIAZ</p>
                  <p>OIC, PENR Officer</p>
               </div>
            </div>

            {/* Footer */}
            <div className="text-xs mt-8">
               <p>***Not Valid without DENR PENRO Marinduque Official Dry Seal***</p>
            </div>
         </div>

         {/* QR Code (remains on both pages) */}
         <div className={`absolute top-4 right-4 ${hiddenOnPrint.includes('qr-code') ? 'no-print' : ''}`}>
            <QRCode
               value={qrValue}
               size={80}
               style={{ height: "auto", maxWidth: "100%", width: "100%" }}
               viewBox={`0 0 80 80`}
            />
            <p className="text-xs mt-1 text-center">Scan to verify</p>
         </div>

         {/* Style to hide scrollbars */}
         <style jsx>{`
            :global(body) {
               -ms-overflow-style: none !important; /* IE and Edge */
               scrollbar-width: none !important; /* Firefox */
               overflow: hidden !important;
            }

            :global(body::-webkit-scrollbar) {
               display: none !important; /* Chrome, Safari, Opera */
            }

            :global(*) {
               scrollbar-width: none !important;
               -ms-overflow-style: none !important;
            }

            :global(*::-webkit-scrollbar) {
               display: none !important;
            }

            :global(html) {
               overflow: hidden !important;
            }
         `}</style>
      </div>
   );
});

PLTCPCertificateTemplate.displayName = 'PLTCPCertificateTemplate';

export default PLTCPCertificateTemplate;
